import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSearch,
  FaClock,
  FaPhone,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaSpinner,
  FaUtensils,
  FaUser,
  FaReceipt,
  FaRupeeSign,
  FaBox,
  FaClipboardList,
  FaExclamationTriangle,
  FaTruck,
  FaCash,
  FaCreditCard,
  FaCalendar,
  FaStore,
  FaMotorcycle,
  FaLock
} from 'react-icons/fa';

import { getData, putData, postData } from '../../../Services/Fetchnodeservices';
import moment from 'moment';

// Constants - Based on your backend data
const ORDER_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  PREPARING: 'preparing',
  READY: 'ready',
  PICKED_UP: 'picked_up',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  PLACED: 'placed',
};

const STATUS_CONFIG = {
  [ORDER_STATUS.PLACED]: { label: 'Placed', color: '#f97316', bg: '#fff7ed', icon: 'clock' },
  [ORDER_STATUS.PENDING]: { label: 'Pending', color: '#f97316', bg: '#fff7ed', icon: 'clock' },
  [ORDER_STATUS.ACCEPTED]: { label: 'Accepted', color: '#ea580c', bg: '#ffedd5', icon: 'check-circle' },
  [ORDER_STATUS.PREPARING]: { label: 'Preparing', color: '#fb923c', bg: '#ffedd5', icon: 'fire' },
  [ORDER_STATUS.READY]: { label: 'Ready', color: '#fdba74', bg: '#fff7ed', icon: 'box' },
  [ORDER_STATUS.PICKED_UP]: { label: 'Picked Up', color: '#f97316', bg: '#fff7ed', icon: 'truck' },
  [ORDER_STATUS.DELIVERED]: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4', icon: 'check-circle' },
  [ORDER_STATUS.CANCELLED]: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', icon: 'times-circle' },
};

const OrdersManagement = ({ isDark = false, onOrderUpdate }) => {
  // State
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [activePeriod, setActivePeriod] = useState('today');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [updating, setUpdating] = useState(false);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    placed: 0,
    accepted: 0,
    preparing: 0,
    ready: 0,
    delivered: 0,
    cancelled: 0
  });

  // Get restaurantId from localStorage
  const getRestaurantId = useCallback(() => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const id = userData?.id || userData?.restaurantId || userData?._id;
        if (id) return id;
      }
      
      const directId = localStorage.getItem('restaurantId');
      if (directId) return directId;
      
      const userId = localStorage.getItem('userId');
      if (userId) return userId;
      
      return null;
    } catch (e) {
      console.error('Error getting restaurantId:', e);
      return null;
    }
  }, []);

  // Apply filter from sessionStorage (from Dashboard clicks)
  const applyFilterFromSession = useCallback((ordersData) => {
    const filterData = sessionStorage.getItem('ordersFilter');
    if (filterData) {
      try {
        const filter = JSON.parse(filterData);
        let filtered = [...ordersData];
        
        if (filter.type === 'pending') {
          filtered = ordersData.filter(o => o.displayStatus === 'pending' || o.displayStatus === 'placed');
          setActiveStatus('pending');
        } else if (filter.type === 'preparing') {
          filtered = ordersData.filter(o => o.displayStatus === 'preparing');
          setActiveStatus('preparing');
        } else if (filter.type === 'todayOrders') {
          const today = new Date().toISOString().split('T')[0];
          filtered = ordersData.filter(o => {
            const orderDate = new Date(o.created_at).toISOString().split('T')[0];
            return orderDate === today;
          });
          setActivePeriod('today');
        } else if (filter.type === 'monthly') {
          const currentMonth = new Date().toISOString().slice(0, 7);
          filtered = ordersData.filter(o => {
            const orderMonth = new Date(o.created_at).toISOString().slice(0, 7);
            return orderMonth === currentMonth;
          });
          setActivePeriod('month');
        } else if (filter.type === 'customers') {
          filtered = ordersData;
        }
        
        sessionStorage.removeItem('ordersFilter');
        return filtered;
      } catch (e) {
        console.error('Error applying filter:', e);
        sessionStorage.removeItem('ordersFilter');
        return ordersData;
      }
    }
    return ordersData;
  }, []);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    const id = getRestaurantId();
    if (!id) {
      setLoading(false);
      setError('Restaurant ID not found. Please login again.');
      return;
    }

    setRestaurantId(id);

    try {
      setLoading(true);
      setError(null);
      const result = await postData('vanderorders/orders/list', { designation_id: id });

      if (result.success) {
        const orderData = result.orders || [];
        
        const processedOrders = orderData.map(order => {
          let parsedItems = [];
          try {
            parsedItems = order.items ? JSON.parse(order.items) : [];
          } catch (e) {
            parsedItems = [];
          }

          const displayStatus = mapBackendStatus(order);

          // Calculate total items price (menu total)
          const itemsTotal = parsedItems.reduce((sum, item) => {
            return sum + (parseFloat(item.price) || 0) * (parseInt(item.qty) || 1);
          }, 0);

          return {
            ...order,
            displayStatus: displayStatus,
            parsedItems: parsedItems,
            itemsTotal: itemsTotal,
            customerName: order.receiver_name || 'Customer',
            customerPhone: order.receiver_phone || '',
            businessAmount: parseFloat(order.business_amount) || 0,
            commissionAmount: parseFloat(order.commission_amount) || 0,
            deliveryCharge: parseFloat(order.delivery_charge) || 0,
            finalAmount: parseFloat(order.final_amount) || 0,
            tax: parseFloat(order.tax) || 0,
            discount: parseFloat(order.discount) || 0,
            platformAmount: parseFloat(order.platform_amount) || 0,
            platformFee: parseFloat(order.platform_fee_amount) || 0,
            handlingCharge: parseFloat(order.handling_charge) || 0,
            handlingFee: parseFloat(order.handling_fee_amount) || 0,
            receivingFee: parseFloat(order.receiving_fee_amount) || 0,
            earnings: parseFloat(order.business_amount) - parseFloat(order.commission_amount),
            created_at: order.created_at,
            updated_at: order.updated_at,
            restroAcceptTime: order.restro_accept_time,
            restroReadyTime: order.restro_ready_time,
            orderDeliveredTime: order.order_delivered_time,
            restroStatus: order.order_status_restro || 'pending',
            userStatus: order.order_status_user || 'placed',
            driverStatus: order.order_status_driver || 'pending',
            payMethod: order.pay_method || 'COD',
            payStatus: order.pay_status || 'PENDING',
            pickupAddress: order.pickup_address || '',
            dropoffAddress: order.dropoff_address || '',
            pickupLat: order.pickup_lat || '',
            pickupLng: order.pickup_lng || '',
            dropoffLat: order.dropoff_lat || '',
            dropoffLng: order.dropoff_lng || '',
            driverName: order.driver_name || '',
            driverContact: order.driver_contact || '',
            pickupOtp: order.pickup_otp || '',
            pickupOtpVerifiedAt: order.pickup_otp_verified_at || '',
            notesForRestro: order.notes_for_restro || '',
            designation: order.designation || 'BUSINESS',
            designationId: order.designation_id || id,
            userid: order.userid || '',
            orderId: order.id,
          };
        });

        setOrders(processedOrders);
        const filtered = applyFilterFromSession(processedOrders);
        setFilteredOrders(filtered);
        updateStats(processedOrders);
      } else {
        setError(result.message || 'Failed to fetch orders');
        setOrders([]);
        setFilteredOrders([]);
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
      setError(err.message || 'Something went wrong');
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  }, [getRestaurantId, applyFilterFromSession]);

  // Map backend status to frontend status
  const mapBackendStatus = (order) => {
    const restroStatus = order.order_status_restro?.toLowerCase() || '';
    const userStatus = order.order_status_user?.toLowerCase() || '';
    const driverStatus = order.order_status_driver?.toLowerCase() || '';

    const statusMap = {
      'accepted': ORDER_STATUS.ACCEPTED,
      'preparing': ORDER_STATUS.PREPARING,
      'ready': ORDER_STATUS.READY,
      'delivered': ORDER_STATUS.DELIVERED,
      'cancelled': ORDER_STATUS.CANCELLED,
      'rejected': ORDER_STATUS.CANCELLED,
      'placed': ORDER_STATUS.PLACED,
      'pending': ORDER_STATUS.PENDING,
      'confirmed': ORDER_STATUS.ACCEPTED,
      'picked_up': ORDER_STATUS.PICKED_UP,
      'assigned': ORDER_STATUS.PREPARING,
    };

    if (statusMap[restroStatus]) return statusMap[restroStatus];
    if (statusMap[userStatus]) return statusMap[userStatus];
    if (statusMap[driverStatus]) return statusMap[driverStatus];
    
    return ORDER_STATUS.PENDING;
  };

  // Update stats
  const updateStats = (ordersData) => {
    setStats({
      total: ordersData.length,
      placed: ordersData.filter(o => o.displayStatus === ORDER_STATUS.PLACED).length,
      accepted: ordersData.filter(o => o.displayStatus === ORDER_STATUS.ACCEPTED).length,
      preparing: ordersData.filter(o => o.displayStatus === ORDER_STATUS.PREPARING).length,
      ready: ordersData.filter(o => o.displayStatus === ORDER_STATUS.READY).length,
      delivered: ordersData.filter(o => o.displayStatus === ORDER_STATUS.DELIVERED).length,
      cancelled: ordersData.filter(o => o.displayStatus === ORDER_STATUS.CANCELLED).length,
    });
  };

  // Initial load
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Filter orders
  useEffect(() => {
    let filtered = [...orders];

    if (activePeriod !== 'all') {
      const now = moment();
      filtered = filtered.filter(order => {
        const orderDate = moment(order.created_at);
        if (activePeriod === 'today') return orderDate.isSame(now, 'day');
        if (activePeriod === 'week') return orderDate.isSame(now, 'week');
        if (activePeriod === 'month') return orderDate.isSame(now, 'month');
        if (activePeriod === 'year') return orderDate.isSame(now, 'year');
        return true;
      });
    }

    if (activeStatus !== 'all') {
      filtered = filtered.filter(order => order.displayStatus === activeStatus);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(order => {
        const orderId = String(order.id || '');
        const customerName = String(order.customerName || '');
        const phone = String(order.customerPhone || '');
        const receiverName = String(order.receiver_name || '');
        const itemsMatch = order.parsedItems?.some(item => item.name?.toLowerCase().includes(query));
        
        return orderId.includes(query) ||
               customerName.toLowerCase().includes(query) ||
               phone.includes(query) ||
               receiverName.toLowerCase().includes(query) ||
               itemsMatch;
      });
    }

    setFilteredOrders(filtered);
  }, [orders, activePeriod, activeStatus, searchQuery]);

  // Get status config
  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG[ORDER_STATUS.PENDING];
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    const id = getRestaurantId();
    if (!id) {
      alert('Restaurant ID not found');
      return false;
    }
    
    try {
      setUpdating(true);
      const result = await putData(`vanderorders/orders/status/${orderId}`, {
        status: newStatus,
        designation_id: id,
        status_type: 'restro'
      });

      if (result.success) {
        await fetchOrders();
        if (onOrderUpdate) onOrderUpdate();
        alert(`Order status updated successfully`);
        return true;
      } else {
        alert(result.message || 'Failed to update status');
        return false;
      }
    } catch (error) {
      console.error('Update status error:', error);
      alert('Error updating status');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const handleAccept = async (order) => {
    if (window.confirm(`Accept order #${order.id}?`)) {
      await updateOrderStatus(order.id, 'accepted');
    }
  };

  const handleReject = async (order) => {
    const reason = prompt('Reason for rejection:', 'Item Unavailable');
    if (reason !== null) {
      await updateOrderStatus(order.id, 'cancelled');
    }
  };

  const handleStatusUpdate = async (order) => {
    let nextStatus;
    let statusLabel;

    switch (order.displayStatus) {
      case ORDER_STATUS.ACCEPTED:
        nextStatus = 'preparing';
        statusLabel = 'Preparing';
        break;
      case ORDER_STATUS.PREPARING:
        nextStatus = 'ready';
        statusLabel = 'Ready';
        break;
      case ORDER_STATUS.READY:
        nextStatus = 'delivered';
        statusLabel = 'Delivered';
        break;
      default:
        return;
    }

    if (window.confirm(`Mark order #${order.id} as "${statusLabel}"?`)) {
      await updateOrderStatus(order.id, nextStatus);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setViewMode('list');
  };

  const handleCall = (phone) => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '0.00';
    return amount.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD MMM YYYY, hh:mm A');
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('hh:mm A');
  };

  // 🎨 ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    success: '#16a34a',
    danger: '#dc2626',
    warning: '#f59e0b',
    shadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(249, 115, 22, 0.15)',
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      backgroundColor: theme.bg,
      minHeight: '100vh',
      transition: 'all 0.3s ease',
    },
    header: {
      marginBottom: '32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerSubtitle: {
      fontSize: '14px',
      color: theme.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
    },
    statBadge: {
      background: theme.hoverBg,
      color: theme.primaryDark,
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 700,
      border: `1px solid ${theme.border}`,
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
      gap: '12px',
      marginBottom: '24px',
    },
    statItem: {
      textAlign: 'center',
      padding: '12px 8px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      transition: 'all 0.3s ease',
      boxShadow: theme.shadow,
    },
    statLabel: {
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: theme.textSecondary,
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 800,
      color: theme.text,
      marginTop: '4px',
    },
    searchContainer: {
      marginBottom: '20px',
    },
    searchWrapper: {
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '14px 16px 14px 44px',
      borderRadius: '12px',
      border: `2px solid ${theme.border}`,
      fontSize: '14px',
      backgroundColor: theme.inputBg,
      color: theme.text,
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      fontWeight: 500,
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.primary,
    },
    periodTabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    },
    periodTab: {
      padding: '8px 20px',
      borderRadius: '20px',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      color: theme.textSecondary,
      transition: 'all 0.2s ease',
    },
    periodTabActive: {
      background: theme.primaryGradient,
      color: '#ffffff',
      borderColor: 'transparent',
      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
    },
    statusTabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      flexWrap: 'wrap',
      borderBottom: `2px solid ${theme.border}`,
      paddingBottom: '12px',
    },
    statusTab: {
      padding: '6px 16px',
      borderRadius: '8px',
      border: 'none',
      background: 'transparent',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      color: theme.textSecondary,
      transition: 'all 0.2s ease',
    },
    statusTabActive: {
      background: theme.primaryGradient,
      color: '#ffffff',
      boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
    },
    ordersList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    orderCard: {
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '20px',
      border: `1px solid ${theme.border}`,
      borderLeftWidth: '5px',
      borderLeftStyle: 'solid',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
    },
    orderHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '8px',
      flexWrap: 'wrap',
      gap: '8px',
    },
    orderId: {
      fontSize: '18px',
      fontWeight: 800,
      color: theme.text,
    },
    orderTime: {
      fontSize: '13px',
      color: theme.textSecondary,
      fontWeight: 500,
    },
    // 🔥 OTP BADGE - Top of card
    otpBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 14px',
      borderRadius: '8px',
      background: 'linear-gradient(135deg, #f97316, #ea580c)',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: 800,
      letterSpacing: '2px',
      boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
      marginBottom: '10px',
      width: 'fit-content',
    },
    orderItems: {
      marginBottom: '12px',
    },
    orderItem: {
      fontSize: '15px',
      color: theme.text,
      padding: '4px 0',
    },
    orderItemQuantity: {
      fontWeight: 700,
      color: theme.primary,
    },
    orderFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',
      paddingTop: '12px',
      borderTop: `1px dashed ${theme.border}`,
    },
    customerName: {
      fontSize: '14px',
      fontWeight: 700,
      color: theme.text,
    },
    earnings: {
      fontSize: '16px',
      fontWeight: 800,
      color: '#16a34a',
      background: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
      padding: '4px 10px',
      borderRadius: '8px',
    },
    statusBadge: (config) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      backgroundColor: config.bg,
      color: config.color,
    }),
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme.textSecondary,
      fontSize: '16px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      gap: '16px',
    },
    loadingSpinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '40px',
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    detailContainer: {
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '24px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
    },
    detailHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    backButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      background: theme.hoverBg,
      color: theme.text,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
    },
    detailSection: {
      marginBottom: '20px',
    },
    detailSectionTitle: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    detailLabel: {
      color: theme.textSecondary,
      fontSize: '14px',
    },
    detailValue: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: 600,
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px',
      flexWrap: 'wrap',
    },
    actionButton: (bg, color) => ({
      padding: '12px 24px',
      borderRadius: '12px',
      border: 'none',
      background: bg,
      color: color || '#ffffff',
      fontSize: '14px',
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)',
    }),
  };

  // Order Detail Component
  const OrderDetail = ({ order, onBack }) => {
    const statusConfig = getStatusConfig(order.displayStatus);
    const isActive = order.displayStatus !== ORDER_STATUS.DELIVERED && order.displayStatus !== ORDER_STATUS.CANCELLED;
    const isPending = order.displayStatus === ORDER_STATUS.PLACED || order.displayStatus === ORDER_STATUS.PENDING;

    // 🔥 Breakdown items - Items shows total menu price, Your Earning shows business_amount
    const breakdownItems = [
      { label: 'Items', value: order.itemsTotal || 0, isNegative: false },
      { label: 'Platform Fee', value: order.platformFee || order.platform_fee_amount || 0, isNegative: true },
      { label: 'Commission', value: order.commissionAmount || order.commission_amount || 0, isNegative: true },
      { label: 'Handling Charges', value: order.handlingFee || order.handling_fee_amount || 0, isNegative: true },
      { label: 'Order Receiving', value: order.receivingFee || order.receiving_fee_amount || 0, isNegative: true },
    ];

    // 🔥 Your Earning = business_amount
    const totalEarning = order.businessAmount || order.business_amount || 0;

    return (
      <div style={styles.detailContainer}>
        <div style={styles.detailHeader}>
          <button 
            style={styles.backButton} 
            onClick={onBack}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.primary}
            onMouseLeave={(e) => e.currentTarget.style.background = theme.hoverBg}
          >
            <FaArrowLeft /> Back to Orders
          </button>
          <div style={styles.statusBadge(statusConfig)}>
            {statusConfig.label}
          </div>
        </div>

        {/* 🔥 OTP at TOP */}
        {order.pickupOtp && (
          <div style={{ ...styles.detailSection, background: isDark ? '#1a1a2e' : '#fef3c7', padding: '16px', borderRadius: '12px', border: `2px solid ${theme.primary}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaLock style={{ color: theme.primary, fontSize: '20px' }} />
                <span style={{ fontWeight: 700, color: theme.text }}>Pickup OTP</span>
              </div>
              <span style={{ fontSize: '24px', fontWeight: 900, letterSpacing: '6px', color: theme.primaryDark }}>
                {order.pickupOtp}
              </span>
              {order.pickupOtpVerifiedAt && (
                <span style={{ fontSize: '12px', color: theme.success }}>
                  ✓ Verified at {formatTime(order.pickupOtpVerifiedAt)}
                </span>
              )}
            </div>
          </div>
        )}

        <div style={styles.detailSection}>
          <h3 style={styles.detailSectionTitle}><FaReceipt style={{color: theme.primary}} /> Order #{order.id}</h3>
          <div style={styles.detailRow}><span style={styles.detailLabel}>Date & Time</span><span style={styles.detailValue}>{formatDate(order.created_at)}</span></div>
          <div style={styles.detailRow}><span style={styles.detailLabel}>Order Type</span><span style={styles.detailValue}>{order.designation || 'BUSINESS'}</span></div>
          <div style={styles.detailRow}><span style={styles.detailLabel}>Payment Method</span><span style={styles.detailValue}>{order.payMethod || 'COD'}</span></div>
          <div style={styles.detailRow}><span style={styles.detailLabel}>Payment Status</span><span style={styles.detailValue}>{order.payStatus || 'PENDING'}</span></div>
        </div>

        <div style={styles.detailSection}>
          <h3 style={styles.detailSectionTitle}><FaUser style={{color: theme.primary}} /> Customer Details</h3>
          <div style={styles.detailRow}><span style={styles.detailLabel}>Name</span><span style={styles.detailValue}>{order.customerName}</span></div>
          {order.customerPhone && (
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Phone</span>
              <span style={styles.detailValue}>
                {order.customerPhone}
                <button style={{ marginLeft: '12px', background: 'none', border: 'none', color: theme.success, cursor: 'pointer', fontSize: '16px' }} onClick={() => handleCall(order.customerPhone)}><FaPhone /></button>
              </span>
            </div>
          )}
        </div>

        {/* 🔥 Order Items - WITHOUT IMAGES */}
        <div style={styles.detailSection}>
          <h3 style={styles.detailSectionTitle}><FaUtensils style={{color: theme.primary}} /> Order Items ({order.parsedItems?.length || 0})</h3>
          {order.parsedItems?.map((item, idx) => (
            <div key={idx} style={styles.detailRow}>
              <span style={styles.detailLabel}>
                {item.qty || 1}x {item.name}
                {item.variant && ` (${item.variant})`}
              </span>
              <span style={styles.detailValue}>₹{(item.price * (item.qty || 1)).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* 🔥 Bill Details - Items shows total menu price, Your Earning shows business_amount */}
        <div style={styles.detailSection}>
          <h3 style={styles.detailSectionTitle}><FaRupeeSign style={{color: theme.primary}} /> Bill Details</h3>
          
          {breakdownItems.map((item, index) => (
            <div key={index} style={styles.detailRow}>
              <span style={styles.detailLabel}>{item.label}</span>
              <span style={{ 
                ...styles.detailValue, 
                color: item.isNegative ? '#dc2626' : theme.text 
              }}>
                {item.isNegative ? `-₹${formatCurrency(item.value)}` : `₹${formatCurrency(item.value)}`}
              </span>
            </div>
          ))}
          
          <div style={{ ...styles.detailRow, borderTop: `2px solid ${theme.primary}`, paddingTop: '12px', marginTop: '8px' }}>
            <span style={{ ...styles.detailLabel, fontWeight: 700, fontSize: '16px' }}>Your Earning</span>
            <span style={{ ...styles.detailValue, fontSize: '18px', fontWeight: 800, color: '#16a34a' }}>
              ₹{formatCurrency(totalEarning)}
            </span>
          </div>
        </div>

        <div style={styles.detailSection}>
          <h3 style={styles.detailSectionTitle}><FaMapMarkerAlt style={{color: theme.primary}} /> Address Details</h3>
          {order.dropoffAddress && (
            <div>
              <div style={styles.detailLabel}>Delivery Address</div>
              <div style={{ padding: '12px', background: theme.hoverBg, borderRadius: '8px', color: theme.text, marginTop: '4px' }}>
                {order.dropoffAddress}
              </div>
            </div>
          )}
        </div>

        {order.notesForRestro && (
          <div style={styles.detailSection}>
            <h3 style={styles.detailSectionTitle}><FaClipboardList style={{color: theme.primary}} /> Special Instructions</h3>
            <div style={{ padding: '12px', background: isDark ? '#1e293b' : '#fef3c7', borderRadius: '8px', color: isDark ? '#e2e8f0' : '#92400e' }}>{order.notesForRestro}</div>
          </div>
        )}

        {isActive && (
          <div style={styles.actionButtons}>
            {isPending ? (
              <>
                <button style={styles.actionButton('#dc2626', '#ffffff')} onClick={() => handleReject(order)} disabled={updating}><FaTimesCircle /> Reject</button>
                <button style={styles.actionButton('linear-gradient(135deg, #16a34a, #059669)', '#ffffff')} onClick={() => handleAccept(order)} disabled={updating}><FaCheckCircle /> Accept Order</button>
              </>
            ) : (
              <>
                {order.displayStatus === ORDER_STATUS.ACCEPTED && (
                  <button style={styles.actionButton('linear-gradient(135deg, #fb923c, #f97316)', '#ffffff')} onClick={() => handleStatusUpdate(order)} disabled={updating}>
                    {updating ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaArrowLeft style={{ transform: 'rotate(90deg)' }} />} Start Preparing
                  </button>
                )}
                {order.displayStatus === ORDER_STATUS.PREPARING && (
                  <button style={styles.actionButton('linear-gradient(135deg, #fdba74, #fb923c)', '#ffffff')} onClick={() => handleStatusUpdate(order)} disabled={updating}>
                    {updating ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaBox />} Mark as Ready
                  </button>
                )}
                {order.displayStatus === ORDER_STATUS.READY && (
                  <button style={styles.actionButton('linear-gradient(135deg, #f97316, #ea580c)', '#ffffff')} onClick={() => handleStatusUpdate(order)} disabled={updating}>
                    {updating ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaTruck />} Mark as Delivered
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.loadingSpinner} />
          <div style={{ color: theme.textSecondary }}>Loading orders...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaExclamationTriangle style={{ fontSize: '48px', marginBottom: '16px', color: theme.danger }} />
          <h3 style={{ color: theme.text }}>Failed to load orders</h3>
          <p>{error}</p>
          <button onClick={fetchOrders} style={{ marginTop: '16px', padding: '8px 24px', background: theme.primaryGradient, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)' }}>Retry</button>
        </div>
      </div>
    );
  }

  if (viewMode === 'detail' && selectedOrder) {
    return (
      <div style={styles.container}>
        <OrderDetail order={selectedOrder} onBack={handleBackToList} />
      </div>
    );
  }

  // List View
  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .order-card:hover { box-shadow: 0 8px 24px rgba(249, 115, 22, 0.1); transform: translateY(-3px); }
      `}</style>

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerTitle}>
            <FaReceipt style={{ color: theme.primary }} /> Order Management
          </div>
          <div style={styles.headerSubtitle}>
            <span style={styles.statBadge}>{filteredOrders.length} Total Orders</span>
            {restaurantId && (
              <span style={{ fontSize: '13px', color: theme.textSecondary }}>
                Restaurant ID: <strong style={{ color: theme.text }}>{restaurantId}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsContainer}>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Total</div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Placed</div>
          <div style={{ ...styles.statValue, color: '#f97316' }}>{stats.placed}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Accepted</div>
          <div style={{ ...styles.statValue, color: '#ea580c' }}>{stats.accepted}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Preparing</div>
          <div style={{ ...styles.statValue, color: '#fb923c' }}>{stats.preparing}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Ready</div>
          <div style={{ ...styles.statValue, color: '#fdba74' }}>{stats.ready}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Delivered</div>
          <div style={{ ...styles.statValue, color: '#16a34a' }}>{stats.delivered}</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statLabel}>Cancelled</div>
          <div style={{ ...styles.statValue, color: '#dc2626' }}>{stats.cancelled}</div>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by Order ID, Customer, Phone, or Item..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Period Tabs */}
      <div style={styles.periodTabs}>
        {['today', 'week', 'month', 'year', 'all'].map((period) => (
          <button
            key={period}
            style={{ ...styles.periodTab, ...(activePeriod === period ? styles.periodTabActive : {}) }}
            onClick={() => setActivePeriod(period)}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)}
          </button>
        ))}
      </div>

      {/* Status Tabs */}
      <div style={styles.statusTabs}>
        {['all', ...Object.values(ORDER_STATUS)].map((status) => {
          const config = getStatusConfig(status);
          return (
            <button
              key={status}
              style={{ ...styles.statusTab, ...(activeStatus === status ? styles.statusTabActive : {}) }}
              onClick={() => setActiveStatus(status)}
            >
              {status === 'all' ? 'All Orders' : config.label}
            </button>
          );
        })}
      </div>

      {/* Orders List */}
      <div style={styles.ordersList}>
        {filteredOrders.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            {searchQuery ? 'No orders found matching your search.' : 'No orders found.'}
          </div>
        ) : (
          filteredOrders.map((order) => {
            const statusConfig = getStatusConfig(order.displayStatus);
            
            return (
              <div
                key={order.id}
                className="order-card"
                style={{
                  ...styles.orderCard,
                  borderLeftColor: statusConfig.color,
                  ...(hoveredOrder === order.id ? { boxShadow: '0 8px 24px rgba(249, 115, 22, 0.1)', transform: 'translateY(-3px)' } : {}),
                }}
                onMouseEnter={() => setHoveredOrder(order.id)}
                onMouseLeave={() => setHoveredOrder(null)}
                onClick={() => handleViewOrder(order)}
              >
                {/* 🔥 OTP AT TOP OF CARD */}
                {order.pickupOtp && (
                  <div style={styles.otpBadge}>
                    <FaLock size={14} />
                    <span>OTP: {order.pickupOtp}</span>
                  </div>
                )}

                <div style={styles.orderHeader}>
                  <span style={styles.orderId}>#{order.id}</span>
                  <span style={styles.orderTime}>
                    {formatTime(order.created_at)} · {moment(order.created_at).format('DD MMM YYYY')}
                  </span>
                </div>

                <div style={styles.orderItems}>
                  {order.parsedItems?.slice(0, 2).map((item, index) => (
                    <div key={index} style={styles.orderItem}>
                      <span style={styles.orderItemQuantity}>{item.qty || 1}x</span> {item.name}
                      {item.variant && ` (${item.variant})`}
                    </div>
                  ))}
                  {order.parsedItems?.length > 2 && (
                    <div style={{ ...styles.orderItem, color: theme.textSecondary, fontSize: '13px' }}>
                      +{order.parsedItems.length - 2} more items
                    </div>
                  )}
                  {order.parsedItems?.length === 0 && (
                    <div style={{ ...styles.orderItem, color: theme.textSecondary, fontSize: '13px', fontStyle: 'italic' }}>
                      No items listed
                    </div>
                  )}
                </div>

                <div style={styles.orderFooter}>
                  <div>
                    <div style={styles.customerName}>
                      <FaUser style={{ fontSize: '12px', marginRight: '4px', opacity: 0.7 }} />
                      {order.customerName}
                    </div>
                    <span style={styles.statusBadge(statusConfig)}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div style={styles.earnings}>
                    ₹{formatCurrency(order.earnings)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;